import clsx from 'clsx'
import Avatar from 'react-avatar'
import Select from 'react-select'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation, useHistory } from 'react-router-dom'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import Chip from '../../UI/Chip'
import Icon from '../../UI/Icon'
import StatusIcon from '../../UI/StatusIcon'

import PopUpList from './components/PopupList'
import VisibilityAccess from '../VisibilityAccess'

import { fullLogOut } from '../../shared/helpers'
import { TRoles, TRoutes, TCompanies, DOCUMENT_TYPES } from '../../shared/const'
import { isAlumni } from '../../shared/alumni'

import { ReactComponent as PageIcon } from '../../assets/icons/page.svg'
import { ReactComponent as SchoolIcon } from '../../assets/icons/school.svg'
import { ReactComponent as SharesIcon } from '../../assets/icons/shares.svg'
import { ReactComponent as LogoutIcon } from '../../assets/icons/Logout.svg'
import { ReactComponent as ExpertIcon } from '../../assets/icons/profile.svg'
import { ReactComponent as ReportsIcon } from '../../assets/icons/reports.svg'
import { ReactComponent as MessageIcon } from '../../assets/icons/message.svg'
import { ReactComponent as TwoUserIcon } from '../../assets/icons/two-user.svg'
import { ReactComponent as SettingsIcon } from '../../assets/icons/settings.svg'
import { ReactComponent as InvoicesIcon } from '../../assets/icons/invoices.svg'
import { ReactComponent as StudentsIcon } from '../../assets/icons/students.svg'
import { ReactComponent as DashboardIcon } from '../../assets/icons/dashboard.svg'
import { ReactComponent as CompaniesIcon } from '../../assets/icons/companies.svg'
import { ReactComponent as DocumentsIcon } from '../../assets/icons/documents.svg'
import { ReactComponent as CommunicationIcon } from '../../assets/icons/communication.svg'
import { ReactComponent as CourseIcon } from '../../assets/icons/course.svg'
/* import { ReactComponent as RoundWarningIcon } from '../../assets/icons/round-warning.svg' */

import logoIcon from '../../assets/icons/logo.svg'

import style from './style.module.scss'
import ReactTooltip from 'react-tooltip'
import { useAvatarUrl } from '../../shared/useAvatarUrls'
import { isTestEnvironment, MAILHOG_URL } from '../../shared/config'
import { showTicketsMenu } from '../../shared/ticketsMenu'
import { PROFILE_DOCUMENTS_TAB, selectMissingDocuments } from '../../shared/missingDocuments'
import useCoursesMenu from './useCoursesMenu'
import { isJmcTeacher } from '../../shared/miniCompanies'

/*
 * In der Testumgebung faerbt sich der Kasten mit dem Namen oben links orange und
 * traegt darunter das Wort "Testumgebung" samt Verweis auf das Sammel-Postfach
 * (MailHog). Der frueher darueber schwebende Hinweis-Tab ist damit entfallen: Er
 * lag zwangslaeufig irgendwo im Weg, waehrend die Kopfecke ohnehin immer im Bild
 * ist und auf jedem Bildschirmfoto mitkommt.
 */
const showTestHint = isTestEnvironment()

// Menüpunkte für Alumni (#213) — eine Auswahl aus dem Menü der Verwaltung.
export const ALUMNI_MENU = [
	TRoutes.DASHBOARD,
	TRoutes.ADMIN_COMPANIES,
	TRoutes.CONCEPT_REPORT,
	TRoutes.SCHOOLS,
	TRoutes.USERS,
	TRoutes.REPORTS_TABLE,
	TRoutes.INVOICES,
	TRoutes.SUPPORTERS,
	TRoutes.SUPPORT_REQUESTS,
	TRoutes.NEWS,
	TRoutes.HANDBOOK
]

function Navbar(props) {
	const { t, i18n } = useTranslation()
	const languageOptions = [
		{ value: 'en', label: 'English' },
		{ value: 'ger', label: 'German' }
	]

	const [activeLanguage, setActiveLanguage] = useState(
		languageOptions.find(({ value }) => value === i18n.language) || languageOptions[0]
	)
	const [isOpenReports, setIsOpenReports] = useState(false)
	// Die Ausklappliste haengt am Menuepunkt und misst dort ihre Lage ab.
	const reportsItemRef = useRef(null)
	// Bild angemeldet holen: ein <img src> schickt keinen Anmelde-Kopfeintrag
	// mit, und die Bild-Route verlangt seit 01.08.2026 eine Anmeldung.
	const avatarSrc = useAvatarUrl(props.avatarHashName)
	const location = useLocation()
	const history = useHistory()
	// „Kurse" erscheint bei Schüler:innen und Lehrkräften nur, wenn es einen gibt.
	const kurseSichtbar = useCoursesMenu(props.role, props.isAuth)
	// Volksschul-Lehrkraft: nur Junior Mini Companies, siehe isJmcTeacher.
	const nurJmc = isJmcTeacher(props.role, props.user.profile)

	const handlePushToMain = () => {
		history.push(TRoutes.MAIN)
	}

	useEffect(() => {
		i18n.changeLanguage('ger')
		setActiveLanguage({ value: 'ger', label: 'German' })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/*
	 * Der untere Block (Profil, Sprache, Abmelden) sass frueher an einer per
	 * JavaScript berechneten Stelle: bei jedem Scrollen wurde ein `top` gesetzt,
	 * damit er knapp ueber dem unteren Bildschirmrand schwebt. Die Rechnung
	 * kannte die fixierte Fusszeile nicht - "Abmelden" lag deshalb zum Teil
	 * darunter - und bei langen Menues (Administration) rutschte der Block ganz
	 * aus dem Bild. Die Leiste ist jetzt eine klebende Spalte in voller
	 * Bildschirmhoehe (siehe style.module.scss); der Block sitzt ohne Rechnerei
	 * an ihrem unteren Ende.
	 */

	useLayoutEffect(() => {
		ReactTooltip.rebuild()
	})

	if (!props.isAuth || location.pathname === TRoutes.AUTH_CONFIRM || location.pathname === TRoutes.MAIN) {
		return (
			<aside className={style.rootUnauthorized}>
				<div className={style.logo} onClick={handlePushToMain}>
					<img src={logoIcon} alt="Logo" />
				</div>
				<div className={style.navProfileContainer}>
					<Select
						isSearchable={false}
						menuPlacement="top"
						classNamePrefix="select"
						className={clsx(style.selectLanguage, style.selectLanguageMini)}
						value={activeLanguage}
						options={languageOptions}
						formatOptionLabel={({ label }) => (
							<span className={style.selectLanguageOption}>
								<Icon name="language" color="#fff" size={{ w: 17, h: 17 }} />
								<span className={clsx(style.selectLanguageLabel, 'option-label')}>{label}</span>
							</span>
						)}
						onChange={option => {
							i18n.changeLanguage(option.value)
							setActiveLanguage(option)
						}}
					/>
					{props.isSignInMode && (
						<NavLink to={TRoutes.MAIN} exact className={style.navExitMini} onClick={fullLogOut}>
							<LogoutIcon className={style.navLogoutIcon} />
						</NavLink>
					)}
				</div>
			</aside>
		)
	}

	const popUpItemsReports = [
		{
			name: t('Semiannual'),
			to: `${TRoutes.REPORTS}?type=semiannual`
		},
		{
			name: t('Annual'),
			to: `${TRoutes.REPORTS}?type=annual`
		}
	]

	const adminLinks = [
		{
			name: t('dashboard'),
			Icon: DashboardIcon,
			to: TRoutes.DASHBOARD,
			count: 0
		},
		{
			name: t('companies'),
			Icon: CompaniesIcon,
			to: TRoutes.ADMIN_COMPANIES,
			// Die Junior Mini Companies stehen seit 31.10.2026 in derselben Liste
			// (#92) — die Zahl zählt die wartenden beider Bestände.
			count:
				(props.user.profile.notifications?.companies || 0) +
				(props.user.profile.notifications?.miniCompanies || 0)
		},
		{
			// Auswertung zum Skills4Retail-Konzept: Fortschritt der
			// Gesamtpopulation und die Liste dahinter. Steht direkt unter den
			// Unternehmen, weil das Konzept an der Junior Company haengt — nicht
			// bei den Auswertungen weiter unten, wo es niemand suchte.
			name: 'Skills4Retail',
			Icon: DocumentsIcon,
			to: TRoutes.CONCEPT_REPORT,
			count: 0
		},
		{
			// Kurse mit Videos und Urkunde: die Bundesverwaltung pflegt sie
			// (Kursverwaltung, von dort auch zur Auswertung), die
			// Landesbetreuung sieht nur die Auswertung ihres Bundeslands.
			name: t('Courses'),
			Icon: CourseIcon,
			to: props.role === TRoles.FEDERAL_ADMIN ? TRoutes.ADMIN_COURSES : TRoutes.COURSE_REPORT,
			count: 0
		},
		// Die eigenen Kurse (Inhalt in Junior) seit 01.11.2026 nur noch zum Testen,
		// nur für Super-Admins — Schüler:innen lernen in den Kursen von TalentsLounge.
		props.user?.profile?.isSuperAdmin && {
			name: t('Courses (test)'),
			Icon: CourseIcon,
			to: TRoutes.COURSES,
			count: 0
		},
		{
			name: t('schools'),
			Icon: SchoolIcon,
			to: TRoutes.SCHOOLS,
			count: 0
		},
		{
			name: t('users'),
			Icon: StudentsIcon,
			to: TRoutes.USERS,
			count: 0
		},
		{
			name: t('reports'),
			Icon: ReportsIcon,
			to: TRoutes.REPORTS_TABLE,
			count: 0
		},

		{
			name: t('invoices'),
			Icon: InvoicesIcon,
			to: TRoutes.INVOICES,
			count: 0
		},
		{
			// Loest den frueheren Menuepunkt "Wirtschaftsexperten" ab: Expert:innen
			// sind jetzt eine Sub-Rolle der Unterstuetzer:innen.
			name: t('supporters'),
			Icon: ExpertIcon,
			to: TRoutes.SUPPORTERS,
			count: 0
		},
		{
			name: t('supportRequests'),
			Icon: MessageIcon,
			to: TRoutes.SUPPORT_REQUESTS,
			count: 0
		},
		{
			name: t('documents'),
			Icon: DocumentsIcon,
			to: TRoutes.ADMIN_DOCUMENTS,
			count: 0
		},
		/*{
			name: t('emailNewsletter'),
			Icon: MessageIcon,
			to: TRoutes.EMAILS,
			count: 0,
			className: style.navItemEmail
		},*/
		{
			name: t('Events & News'),
			className: style.navSeparator,
			Icon: PageIcon,
			to: TRoutes.NEWS,
			count: 0
		},
		{
			// Kommunikation: E-Mail-Vorlagen und interne Nachrichten, die die
			// Anwendung an SuS, LuL und Unterstuetzer:innen verschickt.
			name: t('communication'),
			Icon: CommunicationIcon,
			to: TRoutes.COMMUNICATION,
			count: 0
		},
		{
			// Handbuch: beschreibt die Ablaeufe aller Rollen, richtet sich aber
			// an die Administration.
			name: t('handbook'),
			Icon: DocumentsIcon,
			to: TRoutes.HANDBOOK,
			count: 0
		},
		/*
		 * „Anforderungen & Anfragen" steht auf der Produktion — dort entstehen
		 * sie. Auf der Testumgebung bleibt der Punkt weg, weil beide Umgebungen
		 * getrennte Bestaende haben und dort Erfasstes niemanden erreicht. Die
		 * Einstellung kann das umkehren (shared/ticketsMenu).
		 */
		showTicketsMenu(props.options) && {
			name: t('tickets'),
			Icon: DocumentsIcon,
			to: TRoutes.TICKETS,
			count: 0
		},
		{
			name: t('changelog'),
			Icon: DocumentsIcon,
			to: TRoutes.CHANGELOG,
			count: 0
		},
		{
			// Präsentationen: vertonte Kurzvorstellungen der Plattform für
			// Partner, etwa andere JA-Organisationen in Europa.
			name: t('presentations'),
			Icon: PageIcon,
			to: TRoutes.PRESENTATIONS,
			count: 0
		}
		// Der Menüpunkt „Testprotokolle" ist am 31.10.2026 entfallen: Die
		// Prüfkataloge sind eine Unterlage der Entwicklung (docs/TESTKONZEPT.md),
		// keine Ansicht für die Verwaltung.
	]

	// Ein ausgeblendeter Menuepunkt steht als `false` im Feld — er darf nicht
	// bis zur Darstellung durchkommen.
	const adminLinksSichtbar = adminLinks.filter(Boolean)

	const linksByRole = {
		[TRoles.STUDENT]: [
			{
				name: t('dashboard'),
				Icon: DashboardIcon,
				to: TRoutes.DASHBOARD,
				count: 0
			},
			{
				name: t('companyProfile'),
				Icon: CompaniesIcon,
				to: TRoutes.COMPANY,
				count: 0
			},
			// Die Markt- und Risikoanalyse hatte hier einen eigenen Punkt. Sie
			// beschreibt dasselbe Unternehmen wie das Profil darueber und steht
			// deshalb seit 08.08.2026 als Abschnitt darin — wie im Firmenprofil
			// der Administration.
			{
				name: t('employees'),
				Icon: StudentsIcon,
				to: TRoutes.COMPANY_EMPLOYEE,
				count: 0
			},
			{
				// Schueler:innen sehen weiterhin nur Expert:innen - jetzt aber die
				// Unterstuetzer:innen mit Sub-Rolle Expert statt der alten Liste.
				name: t('Economic experts'),
				Icon: StudentsIcon,
				to: TRoutes.SUPPORTERS,
				count: 0
			},
			{
				// E-Commerce und Digitales Marketing: Lernstrecke und das
				// Konzept, das die Junior Company dazu schreibt.
				name: 'Konzept',
				Icon: DocumentsIcon,
				to: TRoutes.CONCEPT,
				count: 0
			},
			// Nur, wenn es für diese Junior Company einen Kurs gibt.
			kurseSichtbar && {
				name: t('Courses'),
				Icon: CourseIcon,
				to: TRoutes.TL_COURSES,
				count: 0
			},
			{
				name: t('documents'),
				Icon: DocumentsIcon,
				to: TRoutes.DOCUMENTS,
				count: 0
			},
			{
				name: t('shares'),
				Icon: SharesIcon,
				to: TRoutes.SHARES,
				count: 0
			},
			{
				name: t('reports'),
				Icon: ReportsIcon,
				to: TRoutes.REPORTS,
				count: 0,
				popUpList: true
			},
			{
				name: t('Messages'),
				Icon: MessageIcon,
				to: TRoutes.NOTIFICATIONS,
				count: props.user.profile.notifications?.messages
			}
		],
		[TRoles.TEACHER]: [
			{
				name: t('dashboard'),
				Icon: DashboardIcon,
				to: TRoutes.DASHBOARD,
				count: 0
			},
			{
				name: t('companies'),
				Icon: CompaniesIcon,
				to: TRoutes.COMPANIES,
				count: 0
			},
			{
				name: t('students'),
				Icon: StudentsIcon,
				to: TRoutes.STUDENTS,
				count: 0
			},
			{
				// Lehrkraefte sehen dieselbe Expert:innen-Auswahl wie Schueler:innen.
				name: t('Economic experts'),
				Icon: ExpertIcon,
				to: TRoutes.SUPPORTERS,
				count: 0
			},
			{
				// Die betreuende Lehrkraft bewertet hier die fünfzehn
				// Konzeptfelder ihrer Junior Companies.
				name: 'Konzepte bewerten',
				Icon: DocumentsIcon,
				to: TRoutes.CONCEPT,
				count: 0
			},
			kurseSichtbar && {
				name: t('Courses'),
				Icon: CourseIcon,
				to: TRoutes.TL_COURSES,
				count: 0
			},
			{
				name: t('documents'),
				Icon: DocumentsIcon,
				to: TRoutes.DOCUMENTS,
				count: 0
			},
			{
				name: t('reports'),
				Icon: ReportsIcon,
				to: TRoutes.REPORTS_TABLE,
				count: 0
			},
			{
				name: t('Messages'),
				Icon: MessageIcon,
				to: TRoutes.NOTIFICATIONS,
				count: props.user.profile.notifications?.messages
			}
		],
		[TRoles.FEDERAL_ADMIN]: [
			...adminLinksSichtbar,
			{
				name: t('stateAdmins'),
				Icon: TwoUserIcon,
				to: TRoutes.ADMINS,
				count: 0,
				className: style.navItemStateAdmins
			}
		],
		[TRoles.ADMIN]: adminLinksSichtbar,
		/*
		 * Alumni (#213): dieselben Punkte wie die Verwaltung, aber nur die, die
		 * der Server ihnen lesend öffnet (backend `services/alumni/access.js`).
		 * Keine Dokumente, keine Kommunikation, keine Anforderungen, keine
		 * Entwicklungen, keine Administration und keine
		 * Einstellungen.
		 */
		[TRoles.ALUMNI]: adminLinksSichtbar.filter(link => ALUMNI_MENU.includes(link.to)),
		// Unterstuetzer:innen sehen nur ihren eigenen Bereich. Die weiteren
		// Menuepunkte aus dem Mockup (Unternehmen, Schulen, Kontakte, Notizen)
		// waren dort nur zur Veranschaulichung und sind bewusst nicht enthalten.
		[TRoles.SUPPORTER]: [
			{
				name: t('myProfile'),
				Icon: ExpertIcon,
				to: TRoutes.SUPPORTER_PROFILE,
				count: 0
			},
			{
				name: t('myConnections'),
				Icon: CompaniesIcon,
				to: TRoutes.SUPPORTER_CONNECTIONS,
				count: 0
			},
			{
				name: t('supportRequests'),
				Icon: MessageIcon,
				to: TRoutes.SUPPORT_REQUESTS,
				count: 0
			}
		]
	}

	// Ohne diesen Rueckfall wirft die Navigation, sobald eine Rolle kein Menue
	// hinterlegt hat - z.B. wenn spaeter eine weitere Rolle dazukommt.
	const navLinks = (linksByRole[props.role] || [])
		.filter(Boolean)
		// Volksschul-Lehrkraft: Unternehmen (ihre JMC) und Mitteilungen — der Rest
		// gehört zu den Junior Companies (Peter, 02.11.2026).
		.filter(link => !nurJmc || link.to === TRoutes.COMPANIES || link.to === TRoutes.NOTIFICATIONS)

	return (
		<aside className={style.root}>
			<header className={style.header}>
				<div className={clsx(style.logo, showTestHint && style.logoTest)} onClick={handlePushToMain}>
					<img src={logoIcon} alt="Logo" />
				</div>

				{showTestHint ? (
					<div className={style.titleTest}>
						<h1 className={style.titleTestName}>Junior Datenbank</h1>
						<div className={style.testHint}>
							<span className={style.testHintLabel}>{t('Test environment')}</span>

							{/*
							 * Weder in der Adresse noch im Hinweistext stehen Zugangsdaten.
							 *
							 * In der Adresse nicht, weil Chrome eine von der Seite
							 * ausgeloeste Navigation zu `http://benutzer:passwort@…`
							 * stillschweigend abbricht (siehe shared/config.js).
							 *
							 * Im Hinweistext seit 02.09.2026 nicht mehr: Der Streifen
							 * erscheint in jeder Rolle, und was hier steht, steht im
							 * oeffentlich abrufbaren Bundle. Der Browser fragt die
							 * Anmeldung selbst ab; die Verwaltung findet sie unter
							 * Einstellungen -> E-Mail-Versand.
							 */}
							<a
								className={style.testHintMail}
								href={MAILHOG_URL}
								target="_blank"
								rel="noopener noreferrer"
								data-tip={t('Collective mailbox (MailHog)')}
								aria-label={t('Collective mailbox (MailHog)')}
							>
								<MessageIcon className={style.testHintMailIcon} />
							</a>
						</div>
					</div>
				) : (
					<h1 className={style.title}>Junior Datenbank</h1>
				)}
			</header>
			<nav className={style.navigation}>
				{navLinks.map(item =>
					item?.popUpList && props.reportType === TCompanies.JC && props.reportPeriod?.SEMIANNUAL ? (
						<div key={item.name} className={style.navItemWrapper} ref={reportsItemRef}>
							<NavLink
								to={location}
								onClick={() => setIsOpenReports(true)}
								className={clsx(
									style.navItem,
									style.navItemReports,
									location.pathname === TRoutes.REPORTS && style.navItemActive
								)}
							>
								<SettingsIcon className={style.navItemIcon} />
								<span className={style.navItemName}>{t(item.name)}</span>
								<Icon
									className={style.navItemIconSettings}
									name="arrow"
									size={{ w: 11, h: 9 }}
									color="#ffffff"
								/>
							</NavLink>
							<PopUpList
								popUpItems={popUpItemsReports}
								isOpen={isOpenReports}
								onClose={() => setIsOpenReports(false)}
								anchorRef={reportsItemRef}
							/>
						</div>
					) : (
						<NavLink
							key={item.name}
							to={item.to}
							className={clsx(style.navItem, item.className)}
							activeClassName={style.navItemActive}
						>
							<item.Icon className={style.navItemIcon} />
							<span className={style.navItemName}>{item.name}</span>
							{!!item.count && <Chip className={style.navItemCount}>+{item.count}</Chip>}
						</NavLink>
					)
				)}
				{/*
				 * Ohne Ausklappliste. Sie zählte alle rund zwanzig Einstellungen
				 * einzeln auf — als die Einstellungsseite noch eine einzige lange
				 * Liste war und eine Sprungmarke die einzige Art, etwas zu finden.
				 * Seit die Einstellungen hinter Reitern liegen, verdeckte die Liste
				 * nur noch den halben Bildschirm und nannte dieselben Punkte ein
				 * zweites Mal.
				 */}
				{isAlumni(props.role) && (
					// Dezent, aber immer im Bild: Wer hier nichts ändern kann, soll
					// nicht nach dem Knopf suchen.
					<p className={style.readOnlyHint} data-tip={t('readOnlyAccessHint')}>
						<Icon name="lock" color="#C3EDEF" size={{ w: 12, h: 14 }} className={style.readOnlyHintIcon} />
						<span className={style.readOnlyHintLabel}>{t('Read-only access')}</span>
					</p>
				)}
				<VisibilityAccess access={[TRoles.FEDERAL_ADMIN]}>
					<NavLink
						to={TRoutes.SETTINGS}
						exact
						className={style.navItem}
						activeClassName={style.navItemActive}
					>
						<SettingsIcon className={style.navItemIcon} />
						<span className={style.navItemName}>{t('settings')}</span>
					</NavLink>
				</VisibilityAccess>
			</nav>
			<div className={style.navProfileContainer}>
				<div className={style.navProfileRow}>
					<NavLink
						to={TRoutes.PROFILE}
						exact
						className={style.navProfile}
						activeClassName={style.navItemActive}
					>
						<Avatar src={avatarSrc} round name={props.fullName} size={36} className={style.preview} />
						{/* Kein t(): Ein Personenname ist kein Uebersetzungsschluessel. */}
						<span className={style.navProfileName}> {props.fullName}</span>
					</NavLink>

					{/*
					 * Warndreieck am eigenen Bild, solange im Profil ein Dokument
					 * fehlt (Anforderung #182) — die Elternbestaetigung.
					 * Dieselbe Liste wie der Punkt am
					 * Reiter (shared/missingDocuments); ist es hochgeladen, geht
					 * das Dreieck ohne Neuladen weg. Der Klick fuehrt direkt in
					 * den Reiter „Dokumente". Eigener Verweis neben dem Namen,
					 * weil ein Verweis im Verweis kein gueltiges HTML ist.
					 */}
					{props.missingDocumentsCount > 0 && (
						<Link
							to={{ pathname: TRoutes.PROFILE, search: `?tab=${PROFILE_DOCUMENTS_TAB}` }}
							className={style.navProfileWarning}
							aria-label={t('Documents are still missing')}
							data-tip={t('Documents are still missing')}
						>
							<StatusIcon name="warning" size={14} />
						</Link>
					)}
				</div>

				<Select
					isSearchable={false}
					menuPlacement="top"
					classNamePrefix="select"
					className={style.selectLanguage}
					value={activeLanguage}
					options={languageOptions}
					formatOptionLabel={({ label }) => (
						<span className={style.selectLanguageOption}>
							<Icon name="language" color="#fff" size={{ w: 17, h: 17 }} />
							<span className={clsx(style.selectLanguageLabel, 'option-label')}>{label}</span>
						</span>
					)}
					onChange={option => {
						i18n.changeLanguage(option.value)
						setActiveLanguage(option)
					}}
				/>

				{!props.isTeacherStudentView && (
					<NavLink
						to={TRoutes.MAIN}
						exact
						className={style.navExit}
						activeClassName={style.navItemActive}
						onClick={fullLogOut}
					>
						<LogoutIcon className={style.navLogoutIcon} />
						{/*
						 * Eine Beschriftung fuer alle Rollen. Vorher bekam die
						 * Administration eine andere Klasse als der Rest - der
						 * Text stand dadurch je nach Rolle zwei Pixel anders.
						 */}
						<span className={style.navLogoutName}>{t('exit')}</span>
					</NavLink>
				)}

				{/*
				 * Die Rechts-Links (Impressum, Datenschutz, Nutzungsbedingungen,
				 * Barrierefreiheit) standen frueher hier - als vierzeilige, weisse
				 * Liste, die optisch so schwer wog wie die Navigation darueber. Sie
				 * sitzen jetzt in der schmalen Fusszeile: components/AppFooter.
				 */}
			</div>
		</aside>
	)
}

Navbar.propTypes = {
	isAuth: PropTypes.bool.isRequired,
	isSignInMode: PropTypes.bool.isRequired,
	fullName: PropTypes.string.isRequired,
	role: PropTypes.string.isRequired,
	isParentalConfirmation: PropTypes.bool.isRequired,
	reportType: PropTypes.string.isRequired,
	reportPeriod: PropTypes.object.isRequired,
	isTeacherStudentView: PropTypes.bool.isRequired,
	missingDocumentsCount: PropTypes.number
}
Navbar.defaultProps = {
	rejectedParentalConfirmation: false,
	isParentalConfirmation: true
}

const mapStateToProps = state => ({
	isAuth: state.user.isAuth,
	// Die Einstellungen liegen seit dem Start im Store (App.js) — hier zaehlt
	// nur, ob der Menuepunkt „Anforderungen & Anfragen" angezeigt wird.
	options: state.options,
	isSignInMode: state.user.isSignInMode,
	role: state.user.role,
	fullName: `${state.user.profile.firstName} ${state.user.profile.lastName}`,
	user: state.user,
	avatarHashName: state.user.profile.avatarHashName,
	reportType: state.user.profile.EmploymentCompany?.companyType,
	isParentalConfirmation: !!state.user.profile.Documents?.find(
		({ type }) => type === DOCUMENT_TYPES.PARENTAL_CONFIRMATION
	),
	reportPeriod: state.user.profile.reportPeriod,
	missingDocumentsCount: selectMissingDocuments(state).length,
	rejectedParentalConfirmation: state.user.profile.rejectedParentalConfirmation,
	emailConfirmed: state.user.profile.emailConfirmed,
	isTeacherStudentView: state.user.isTeacherStudentView
})

export default connect(mapStateToProps)(Navbar)
